import React, {useContext, useState} from 'react';
import {
    Toolbar,
    Typography,
    Tooltip,
    IconButton,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Container,
} from '@material-ui/core';
import {lighten, makeStyles} from '@material-ui/core/styles';
import clsx from 'clsx';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import {LoopContext} from 'components/forms/context';
import {useFormPersistence} from 'components/forms/hooks';
import get from 'lodash.get';

const useStyles = makeStyles(theme => ({
    highlight:
        theme.palette.type === 'light'
            ? {
                  color: theme.palette.secondary.main,
                  backgroundColor: lighten(theme.palette.secondary.light, 0.85),
              }
            : {
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.secondary.dark,
              },
    grow: {
        flexGrow: 1,
    },
    title: {
        flex: '1 1 100%',
    },
    numberInput: {
        textAlign: 'right',
    },
}));

const GenericLoopIndex = props => {
    const looper = useContext(LoopContext);

    const classes = useStyles();

    // UI state
    const {state} = useFormPersistence();
    const data = get(state, looper?.node) || {};
    const [selected, setSelected] = useState(null);

    const isSelected = index => selected === index;

    const handleRowClick = (event, index) => {
        setSelected(index);
    };

    const editSelected = () => {
        looper.setIndex(selected);
        looper.goTo(1);
    };

    const deleteSelected = () => {
        looper.remove(selected);
        setSelected(null);
    };

    const handleAdd = () => {
        looper.addNew();
        looper.goTo(1); // Go to next (first real section)
    };

    return looper ? (
        <Container>
            <Typography variant='h4'>
                {props.title || `Loop Controller for ${looper?.node}`}
            </Typography>
            <Toolbar
                disableGutters
                className={clsx(classes.root, {
                    [classes.highlight]: selected !== null,
                })}
            >
                {selected !== null && (
                    <>
                        <Typography className={classes.title}>
                            Edit or Delete selection?
                        </Typography>
                        <Tooltip title='Edit'>
                            <IconButton
                                aria-label='edit'
                                onClick={editSelected}
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                            <IconButton
                                aria-label='delete'
                                onClick={deleteSelected}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Toolbar>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell align='center'>Item</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Object.keys(data).map(index => {
                            const rowIsSelected = isSelected(index);
                            if (!Object.keys(data[index]).length) {
                                return null;
                            }
                            return (
                                <TableRow
                                    hover
                                    role='checkbox'
                                    onClick={event =>
                                        handleRowClick(event, index)
                                    }
                                    aria-checked={rowIsSelected}
                                    tabIndex={-1}
                                    key={index}
                                    selected={rowIsSelected}
                                >
                                    <TableCell align='right'>
                                        {JSON.stringify(data[index])}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        <TableRow>
                            <TableCell>
                                <Tooltip title='Add'>
                                    <IconButton
                                        onClick={handleAdd}
                                        edge='start'
                                        size='small'
                                        color='secondary'
                                        data-cy={`form-field-loop-add`}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>
    ) : (
        <div>This section requires a loop parent.</div>
    );
};

export default GenericLoopIndex;
